import { AccountType } from "@shared/contexts/AccountContext";
import { currentTimestamp } from "../../utils/time/delay";
import { isSuccessTokenStepResponse } from "../../utils/typeGuards";
import { ApiError, RedirectionResponse, SuccessTokenStepResponse } from "@shared/services/auth";

import cache from "memory-cache";
import sha256 from "crypto-js/sha256";
import { AuthType } from "@shared/contexts/AuthenticationContext";

export const PERSONAL_INFO_POST_RESPONSE = "personalInfoPostResponse";
export const IDENTITY_POST_RESPONSE = "identityPostResponse";
export const PASSWORD_POST_RESPONSE = "passwordPostResponse";

const POST_RESPONSE_LIST = [PERSONAL_INFO_POST_RESPONSE, IDENTITY_POST_RESPONSE, PASSWORD_POST_RESPONSE];

export interface CachedResponse {
  personalInfoPostResponse?: ApiError | SuccessTokenStepResponse;
  identityPostResponse?: ApiError | SuccessTokenStepResponse;
  passwordPostResponse?: ApiError | SuccessTokenStepResponse;
}

type Post_type =
  | typeof PERSONAL_INFO_POST_RESPONSE
  | typeof IDENTITY_POST_RESPONSE
  | typeof PASSWORD_POST_RESPONSE;

export function calculateHash(value: string): string {
  return sha256(value).toString();
}

export class CacheService {
  post_type: Post_type;
  hash_key: string;

  cachedResponse: CachedResponse = {
    personalInfoPostResponse: undefined,
    identityPostResponse: undefined,
    passwordPostResponse: undefined,
  };

  constructor(currentDatas: AccountType | AuthType, post_type: Post_type, accountData: AccountType | AuthType) {
    this.post_type = post_type;

    const { step, ...accountDatas } = currentDatas;
    this.hash_key = calculateHash(JSON.stringify(accountDatas));

    if (cache.get(this.hash_key) === null) this.createCache(this.hash_key, accountData);
    else this.cachedResponse = cache.get(this.hash_key);
  }

  createCache(hash_key: string, accountData: AccountType | AuthType) {
    const { step, ...accountDatas } = accountData;
    const previous_data = cache.get(calculateHash(JSON.stringify(accountDatas)));
    const valid_types = POST_RESPONSE_LIST.slice(0, POST_RESPONSE_LIST.indexOf(this.post_type));
    
    if (previous_data) valid_types.forEach((type) => {
      this.cachedResponse[type as keyof CachedResponse] = previous_data[type];
    });

    cache.put(hash_key, this.cachedResponse, 420000);
  }

  postToCache(requestResponse: ApiError | SuccessTokenStepResponse | RedirectionResponse) {
    this.cachedResponse = {
      ...this.cachedResponse,
      [this.post_type]: requestResponse,
    };

    cache.put(this.hash_key, this.cachedResponse, 420000);
  }

  private isTokenValid(requestResponse: SuccessTokenStepResponse): boolean {
    const currentTime = currentTimestamp();
    return requestResponse.expiration_time > (currentTime + 30);
  }

  private removeExpiredToken(post_type: Post_type): void {
    console.warn(`Token expired for ${post_type}, removing from cache`);
    this.cachedResponse[post_type] = undefined;
    cache.put(this.hash_key, this.cachedResponse, 420000);
  }

  private getCachedResponseForType(post_type: Post_type): any {
    const response = cache.get(this.hash_key);
    return response?.[post_type];
  }

  getToCache(post_type: Post_type = this.post_type) {
    const requestResponse = this.getCachedResponseForType(post_type);
    if (!requestResponse) return undefined;

    if (!isSuccessTokenStepResponse(requestResponse)) return requestResponse;

    if (this.isTokenValid(requestResponse)) return requestResponse;
    this.removeExpiredToken(post_type);
    return undefined;
  }

}

